import { t } from "i18next";
import { validator } from "../../../services/form-validator/form-validator";
import { api } from "../../../services/api/api";
import AsyncSelect from "react-select/async";
import { PartValidator } from "./part-validator";
import { isOdooConnected } from "../../../services/odoo/odoo";
import { useUserRoles } from "../../../hooks/useUserRoles";

export const PartFormValidator = {
  category: (e: EasyTech.PartCategory) => !e ? true : validator.isString(e.label),
  label: (e: unknown) => validator.isString(e),
  price: (e: unknown) => validator.isString(e) || validator.isNumber(e),
  ref: (e: unknown) => !e || validator.isString(e), // ref n'est plus obligatoire
  unity: (e: unknown) => validator.isString(e)
};

/**
* PartForm: Create/edit part form 
* @param data - Data to edit
* @param onChange - On change event, trigger on field change
* @param onSubmit - On submit event, trigger enter key down on field
* @param submitted - is form submitted
*/
export default function PartForm({
  data,
  onChange,
  onSubmit,
  submitted,
  isEditing
}: {
  data?: EasyTech.Part,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean
}) {
  const { isSuperAdmin } = useUserRoles();

  /*
   * L'identifiant Odoo n'est modifiable qu'en édition, et seulement par un super admin sur un realm
   * relié à Odoo — ailleurs le catalogue est maître dans EasyTech et le champ n'aurait rien à
   * désigner. Absent en création : un article créé ici n'a pas encore de contrepartie Odoo.
   */
  const canEditExternalId = isEditing === true && isSuperAdmin && isOdooConnected();

  const UNITY_OPTIONS = [
    { value: "m", label: "meter(s)" },
    { value: "l", label: "liter(s)" },
    { value: "u", label: "unit(s)" }
  ];

  const loadCategoryOptions = (inputValue: string, callback: any) => {
    api.get("users/parts_categories").then((categories: any) => {
      const filtered = categories
        .filter((cat: any) =>
          (cat.label && cat.label.toLowerCase().includes(inputValue.toLowerCase())) ||
          (cat.ref && cat.ref.toLowerCase().includes(inputValue.toLowerCase()))
        )
        .map((cat: any) => ({ value: cat.id, label: cat.label, ref: cat.ref }));
      callback(filtered);
    });
  };

  const loadUnityOptions = (inputValue: string, callback: any) => {
    const filtered = UNITY_OPTIONS.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    callback(filtered);
  };

  const getUnityValue = (unity: string) => {
    let option = UNITY_OPTIONS.find(opt => opt.value === unity);

    if (!option) {
      option = UNITY_OPTIONS.find(opt => opt.label === unity);
    }

    return option || null;
  };

  const validateFormData = (formData: any): boolean => {
    if (!formData) return false;
    const validationErrors = PartValidator.validate(formData, t);
    return validationErrors.length === 0;
  };

  const handleSubmitWithValidation = () => {
    if (submitted && !validateFormData(data)) {
      console.warn("Validation échouée - champs obligatoires manquants");
      return;
    }
    onSubmit();
  };

  return (
    <>
      <div className="field">
        <label htmlFor="label">{t("piece:label")} *</label>
        <input type="text"
                className={`form-field ${(submitted && data && !validator.isFieldValid(data, "label", PartFormValidator)) && "error"}`}
                onChange={(value) => onChange("label", value.target.value)}
                onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                value={data?.label || ""} />
      </div>

      {canEditExternalId && (
        <div className="field">
          <label htmlFor="externalId">{t("piece:odoo:externalId:label")}</label>

          {/* value plutôt que defaultValue : vider le champ doit remonter "" jusqu'au parent, qui
              le lit comme « délier cet article d'Odoo ». */}
          <input type="text"
                  id="externalId"
                  name="externalId"
                  className="form-field"
                  placeholder={t("piece:odoo:externalId:placeholder")}
                  value={data?.externalId ?? ""}
                  onChange={(value) => onChange("externalId", value.target.value)}
                  onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()} />

          <div className="field-hint">
            <span className="text-xs text-gray-500">{t("piece:odoo:externalId:help")}</span>
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="ref">{t("piece:ref")}</label>
        
        <input type="text"
                className={`form-field ${(submitted && data && !validator.isFieldValid(data, "ref", PartFormValidator)) && "error"}`}
                onChange={(value) => onChange("ref", value.target.value)}
                onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                value={data?.ref || ""} />
      </div>
      
      <div className="field">
        <label htmlFor="price">{t("piece:price")} *</label>
        
        <input type="number"
                className={`form-field ${(submitted && data && !validator.isFieldValid(data, "price", PartFormValidator)) && "error"}`}
                onChange={(value) => onChange("price", value.target.value)}
                onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}
                value={data?.price || ""} />
      </div>

      <div className="field">
        <label htmlFor="email">{t("piece:category")}</label>
        <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCategoryOptions}
                  value={
                    data?.category?.id
                      ? {
                        value: data.category.id,
                        label: data.category.label,
                        ref: data.category.ref,
                      }
                      : null
                  }
                  onChange={(option: any) => {
                    if (option) {
                      onChange("category", {
                        id: option.value,
                        label: option.label,
                        ref: option.ref,
                      });
                    } else {
                      onChange("category", {});
                    }
                  }}
                  placeholder={t("piece:select_category")}
                  isClearable
          />
      </div>

      <div className="field">
        <label htmlFor="unity">{t("piece:unity")} *</label>
        <AsyncSelect
          cacheOptions
          defaultOptions
          loadOptions={loadUnityOptions}
          value={data?.unity ? getUnityValue(data.unity) : null}
          onChange={(option: any) => {
            if (option) {
              onChange("unity", option.value);
            } else {
              onChange("unity", "");
            }
          }}
          placeholder={t("piece:select_unity")}
          isClearable
          classNamePrefix="react-select"
          className={`${(submitted && data && !validator.isFieldValid(data, "unity", PartFormValidator)) ? "error" : ""}`}
        />
      </div>
    </>
  );
}
